
import * as React from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Drawer from "@mui/material/Drawer";
import { Stack, Box, Typography, Grid, IconButton, MenuItem, FormControl, InputLabel, Select, FormHelperText, Divider, Checkbox, FormControlLabel, Autocomplete } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SearchInput from "../../components/SearchInput";
import { useForm } from "react-hook-form";
import { useSlotStore } from "../../services/slots";
import { ISlot, CreateSlotRequest } from "../../types/slots";
import { useWellnessPackageStore } from "../../services/wellnessPackages";
import { SLOT_TYPES, RECURRING_PATTERNS } from "./constants";
import { showError } from "../../services/toaster";
import dayjs from "dayjs";
import { doGET } from "../../utils/HttpUtils";
import { useAppContext } from "../../services/context/AppContext";
import { getUserSpecialistId, isDoctorUser } from "../../utils/helper";

const convertTimeTo12Hour = (time24: string) => {
    if (!time24) return time24;
    const [hourStr = "0", minute = "00"] = time24.split(":");
    const hour = Number(hourStr);
    if (Number.isNaN(hour)) return time24;

    const period = hour >= 12 ? "PM" : "AM";
    const normalizedHour = hour % 12 || 12;
    return `${normalizedHour}:${minute} ${period}`;
};

const convertTimeTo24Hour = (time12: string) => {
    if (!time12) return time12;
    if (/^\d{2}:\d{2}$/.test(time12)) return time12;

    const match = time12.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return time12;

    const [, hourPart, minute, periodPart] = match;
    const period = periodPart.toUpperCase();
    let hour = Number(hourPart);

    if (period === "AM") {
        hour = hour === 12 ? 0 : hour;
    } else {
        hour = hour === 12 ? 12 : hour + 12;
    }

    return `${String(hour).padStart(2, "0")}:${minute}`;
};

const convertTimeToMinutes = (timeValue: string) => {
    const normalizedTime = convertTimeTo24Hour(timeValue);
    const [hourStr = "0", minuteStr = "0"] = normalizedTime.split(":");
    const hour = Number(hourStr);
    const minute = Number(minuteStr);

    if (Number.isNaN(hour) || Number.isNaN(minute)) return NaN;
    return hour * 60 + minute;
};

export default function AddSlotsDialog({
    isModalOpen,
    toggleModal,
    selectedId,
    hideAddButton = false
}: any) {
    const { userData } = useAppContext();
    const isDoctor = isDoctorUser(userData);
    const loggedInSpecialistId = getUserSpecialistId(userData);
    const defaultData: Partial<ISlot> = {
        date: dayjs().format('YYYYMMDD'),
        startTime: "09:00",
        endTime: "10:00",
        duration: 60,
        isAvailable: true,
        maxBookings: 1,
        currentBookings: 0,
        slotType: 'consultation',
        isRecurring: false,
        recurringPattern: 'daily',
        isActive: true,
        specialistId: isDoctor ? loggedInSpecialistId : '',
        wellnessPackageId: ''
    }

    const { onCreate, onCreateSelf, detail, onUpdate, onUpdateSelf, filters, setFilters, data: slotsList, fetchSuggestions } = useSlotStore();

    const [optionPool, setOptionPool] = React.useState<any[]>([]);


    React.useEffect(() => {
        const loadPool = async () => {
            console.log(`[SlotsFilter] Context changed. Fetching fresh options for SlotType: ${filters.slotType}, Specialist: ${filters.specialistId}`);
            setOptionPool([]); // Reset immediately on context change
            
            const data = await fetchSuggestions({
                specialistId: filters.specialistId,
                wellnessPackageId: filters.wellnessPackageId,
                slotType: filters.slotType
            });
            
            if (data && data.length > 0) {
                console.log(`[SlotsFilter] Pool populated with ${data.length} comprehensive items`);
                setOptionPool(data);
            }
        };
        loadPool();
    }, [filters.specialistId, filters.wellnessPackageId, filters.slotType, fetchSuggestions]);

    React.useEffect(() => {
        if (slotsList.length > 0) {
            setOptionPool(prev => {
                const existingIds = new Set(prev.map(p => p._id));
                const newItems = slotsList.filter(s => !existingIds.has(s._id));
                if (newItems.length === 0) return prev;
                return [...prev, ...newItems];
            });
        }
    }, [slotsList]);

    // DERIVED DROPDOWN DATA
    const uniqueDates = React.useMemo(() => {
        const dateSet = new Set<string>();
        const source = optionPool.length > 0 ? optionPool : slotsList;
        source.forEach((s: any) => { if (s.date) dateSet.add(s.date); });
        if (filters.date) dateSet.add(filters.date);
        return Array.from(dateSet).sort();
    }, [optionPool, slotsList, filters.date]);

    const uniqueTimes = React.useMemo(() => {
        // Narrow down pool by selected date before extracting times
        const sourceData = optionPool.length > 0 ? optionPool : slotsList;
        const filteredByDate = filters.date 
            ? sourceData.filter((s: any) => s.date === filters.date)
            : sourceData;

        const timeSet = new Set<string>();
        filteredByDate.forEach((s: any) => { if (s.startTime) timeSet.add(s.startTime); });
        // Always ensure selected time is in the list
        const currentStartTime = (filters as any).startTime;
        if (currentStartTime) timeSet.add(currentStartTime);
        return Array.from(timeSet).sort();
    }, [optionPool, slotsList, filters.date, (filters as any).startTime]);
    const { data: wellnessPackages, fetchGrid: fetchWellnessPackages } = useWellnessPackageStore();
    const [specialists, setSpecialists] = React.useState<any[]>([]);
    const [isLoadingSpecialists, setIsLoadingSpecialists] = React.useState(false);
    const specialistOptions = React.useMemo(
        () => {
            if (isDoctor && loggedInSpecialistId) {
                return [{
                    value: loggedInSpecialistId,
                    label: userData?.name || userData?.email || userData?.phone || 'My profile',
                }];
            }

            return specialists.map((specialist) => ({
                value: specialist._id || '',
                label: `${specialist.name || 'Unknown'}${specialist.specialization ? ` (${specialist.specialization})` : ''}`,
            }));
        },
        [isDoctor, loggedInSpecialistId, specialists, userData?.email, userData?.name, userData?.phone]
    );

    const [slotData, setSlotData] = React.useState<Partial<ISlot>>(defaultData)

    const handleChange = (key: keyof ISlot, value: any) => {
        setSlotData(prev => ({ ...prev, [key]: value }));
        if (fieldErrors[key]) setFieldErrors(prev => ({ ...prev, [key]: '' }));
    }

    const [fieldErrors, setFieldErrors] = React.useState<{ [key: string]: string }>({});

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset
    } = useForm<CreateSlotRequest>();

    const fetchAllSpecialists = React.useCallback(async () => {
        if (isDoctor) {
            setSpecialists([]);
            return;
        }

        setIsLoadingSpecialists(true);
        try {
            const mergedSpecialists: any[] = [];
            const seenIds = new Set<string>();
            let page = 1;
            let totalPages = 1;
            const limit = 100;

            do {
                const response = await doGET(`/specialists?page=${page}&limit=${limit}`);
                if (!(response.status >= 200 && response.status < 400)) {
                    showError(response.message || 'Failed to fetch specialists');
                    break;
                }

                const rows = Array.isArray(response.data?.data) ? response.data.data : [];
                totalPages = Number(response.data?.pagination?.totalPages) || 1;

                rows.forEach((specialist: any) => {
                    const specialistId = specialist?._id;
                    if (specialistId && !seenIds.has(specialistId)) {
                        seenIds.add(specialistId);
                        mergedSpecialists.push(specialist);
                    }
                });

                page += 1;
            } while (page <= totalPages);

            setSpecialists(mergedSpecialists);
        } catch (_error) {
            showError('Failed to fetch specialists');
        } finally {
            setIsLoadingSpecialists(false);
        }
    }, [isDoctor]);

    React.useEffect(() => {
        fetchAllSpecialists();
        fetchWellnessPackages();
    }, [fetchAllSpecialists, fetchWellnessPackages]);

    const handleClickOpen = () => {
        setSlotData({
            ...defaultData,
            slotType: 'consultation',
            specialistId: isDoctor ? loggedInSpecialistId : defaultData.specialistId,
            wellnessPackageId: '',
        });
        toggleModal(true);
    };

    const handleClose = () => {
        toggleModal(false);
        setSlotData(defaultData);
    };

    const handleFilterChange = (key: string, value: any) => {
        if (isDoctor && key === 'specialistId') return;
        setFilters({ ...filters, [key]: value || undefined });
    };

    const toggleSortOrder = () => {
        const currentOrder = filters.sortOrder || 'asc';
        setFilters({ ...filters, sortOrder: currentOrder === 'asc' ? 'desc' : 'asc' });
    };

    const validateForm = (): boolean => {
        const errs: { [key: string]: string } = {};

        if (!slotData.date) {
            errs.date = 'Date is required';
        }

        if (!slotData.startTime) {
            errs.startTime = 'Start time is required';
        }

        if (!slotData.endTime) {
            errs.endTime = 'End time is required';
        } else if (slotData.startTime && slotData.endTime) {
            const startMinutes = convertTimeToMinutes(slotData.startTime);
            const endMinutes = convertTimeToMinutes(slotData.endTime);

            if (!Number.isNaN(startMinutes) && !Number.isNaN(endMinutes) && endMinutes <= startMinutes) {
                errs.endTime = 'End time must be after start time';
            }
        }

        if (slotData.duration !== undefined && slotData.duration !== null && String(slotData.duration) !== '') {
            const parsedDuration = Number(slotData.duration);
            if (parsedDuration < 1) {
                errs.duration = 'Duration must be at least 1 minute';
            } else if (parsedDuration > 480) {
                errs.duration = 'Duration cannot exceed 480 minutes';
            }
        }

        if (slotData.maxBookings !== undefined && slotData.maxBookings !== null && String(slotData.maxBookings) !== '') {
            const parsedMax = Number(slotData.maxBookings);
            if (parsedMax < 1) {
                errs.maxBookings = 'Max bookings must be at least 1';
            }
        }

        if (slotData.slotType === 'consultation' && !(isDoctor ? loggedInSpecialistId : slotData.specialistId)) {
            errs.specialistId = 'Please select a specialist';
        }

        if (slotData.slotType === 'wellnessPackage' && !slotData.wellnessPackageId) {
            errs.wellnessPackageId = 'Please select a wellness package';
        }

        setFieldErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const onSubmit = () => {
        if (!validateForm()) {
            showError('Please fix the highlighted errors');
            return;
        }

        // Format date if needed (YYYY-MM-DD to YYYYMMDD)
        const formattedDate = slotData.date ? slotData.date.replace(/-/g, '') : '';

        // Construct payload
        const payload: any = {
            ...slotData,
            date: formattedDate,
            startTime: convertTimeTo12Hour(slotData.startTime || ''),
            endTime: convertTimeTo12Hour(slotData.endTime || ''),
            // Ensure specific fields are cleaned based on type
            specialistId: slotData.slotType === 'consultation' ? (isDoctor ? loggedInSpecialistId : slotData.specialistId) : undefined,
            wellnessPackageId: slotData.slotType === 'wellnessPackage' && !isDoctor ? slotData.wellnessPackageId : undefined,
        };

        if (slotData?._id) {
            if (isDoctor) {
                onUpdateSelf(payload as ISlot);
            } else {
                onUpdate(payload as ISlot);
            }
        } else {
            if (isDoctor) {
                onCreateSelf(payload as CreateSlotRequest);
            } else {
                onCreate(payload as CreateSlotRequest);
            }
        }
        handleClose();
    };

    const fetchDetail = async (selectedId: string) => {
        try {
            const data = await detail(selectedId);
            if (data?.data) {
                // Format date for input (YYYYMMDD to YYYY-MM-DD)
                const dateStr = data.data.date;
                const formattedDate = dateStr && dateStr.length === 8
                    ? `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`
                    : dateStr;

                setSlotData({
                    ...data.data,
                    date: formattedDate,
                    startTime: convertTimeTo24Hour(data.data.startTime || ''),
                    endTime: convertTimeTo24Hour(data.data.endTime || '')
                });
            }
        } catch (error) {
            console.error(error);
        }
    }

    React.useEffect(() => {
        if (selectedId && isModalOpen) {
            fetchDetail(selectedId)
        } else if (!selectedId && isModalOpen) {
            setSlotData({
                ...defaultData,
                slotType: 'consultation',
                specialistId: isDoctor ? loggedInSpecialistId : defaultData.specialistId,
                wellnessPackageId: '',
            });
        }
    }, [selectedId, isModalOpen, isDoctor, loggedInSpecialistId]);

    return (
        <div>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}
                >
                    <SearchInput />
                    {!hideAddButton && (
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={handleClickOpen}
                        >
                            Add Slot
                        </Button>
                    )}
                </Stack>

                {/* All Filters: Slot Type + Specialist + Wellness Package + Sort By + Date + Time */}
                <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="center">
                    {!isDoctor && (
                        <>
                            {/* Slot Type Filter */}
                            <FormControl size="small" sx={{ minWidth: 130 }}>
                                <InputLabel id="filter-slot-type">Slot Type</InputLabel>
                                <Select
                                    labelId="filter-slot-type"
                                    label="Slot Type"
                                    value={filters.slotType || ''}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        const updatedFilters: any = { ...filters, slotType: val || undefined };
                                        if (val !== 'consultation') updatedFilters.specialistId = undefined;
                                        if (val !== 'wellnessPackage') updatedFilters.wellnessPackageId = undefined;
                                        setFilters(updatedFilters);
                                    }}
                                    renderValue={(selected) => {
                                        if (!selected) return 'All';
                                        const found = SLOT_TYPES.find(t => t.value === selected);
                                        return found ? found.label : selected;
                                    }}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    {SLOT_TYPES.map((type) => (
                                        <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* Specialist Filter — enabled only when slotType = consultation */}
                            <Autocomplete
                                size="small"
                                sx={{ minWidth: 220 }}
                                options={specialistOptions}
                                disabled={filters.slotType !== 'consultation' || isLoadingSpecialists}
                                value={specialistOptions.find((option) => option.value === (filters.specialistId || '')) || null}
                                onChange={(_event, option) => handleFilterChange('specialistId', option?.value || '')}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                getOptionLabel={(option) => option.label}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Specialist"
                                        helperText={filters.slotType !== 'consultation' ? 'Choose consultation type first' : ''}
                                    />
                                )}
                            />

                            {/* Wellness Package Filter — enabled only when slotType = wellnessPackage */}
                            <FormControl size="small" sx={{ minWidth: 150 }} disabled={filters.slotType !== 'wellnessPackage'}>
                                <InputLabel id="filter-package">Wellness Package</InputLabel>
                                <Select
                                    labelId="filter-package"
                                    label="Wellness Package"
                                    value={filters.wellnessPackageId || ''}
                                    onChange={(e) => handleFilterChange('wellnessPackageId', e.target.value)}
                                    renderValue={(selected) => {
                                        if (!selected) return 'All';
                                        const found = wellnessPackages.find(w => w._id === selected);
                                        return found ? found.name : selected;
                                    }}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    {wellnessPackages.map(w => <MenuItem key={w._id} value={w._id}>{w.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </>
                    )}
                    {/* Sort By */}
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Sort By</InputLabel>
                        <Select
                            value={filters.sortBy || ''}
                            label="Sort By"
                            onChange={(e) => {
                                const val = e.target.value;
                                setFilters({ ...filters, sortBy: val || undefined });
                            }}
                        >
                            <MenuItem value="">No Sort</MenuItem>
                            <MenuItem value="date">Date</MenuItem>
                            <MenuItem value="startTime">Time</MenuItem>
                            {/* <MenuItem value="slotType">Slot Type</MenuItem> */}
                            {/* <MenuItem value="isAvailable">Availability</MenuItem> */}
                        </Select>
                    </FormControl>

                    <IconButton onClick={toggleSortOrder} color="primary" title={`Current sort order: ${filters.sortOrder || 'asc'}`}>
                        {filters.sortOrder === 'desc' ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />}
                    </IconButton>

                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Filter by Date</InputLabel>
                        <Select
                            value={filters.date || ''}
                            label="Filter by Date"
                            onChange={(e) => handleFilterChange('date', e.target.value)}
                            renderValue={(selected) => {
                                if (!selected) return 'All Dates';
                                return dayjs(selected).format('DD MMM YYYY');
                            }}
                        >
                            <MenuItem value="">All Dates</MenuItem>
                            {uniqueDates.map(date => (
                                <MenuItem key={date} value={date}>
                                    {dayjs(date).format('DD MMM YYYY')}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Filter by Time</InputLabel>
                        <Select
                            value={filters.startTime || ''}
                            label="Filter by Time"
                            onChange={(e) => handleFilterChange('startTime', e.target.value)}
                            renderValue={(selected) => {
                                if (!selected) return 'All Times';
                                return selected;
                            }}
                        >
                            <MenuItem value="">All Times</MenuItem>
                            {uniqueTimes.map(t => (
                                <MenuItem key={t} value={t}>{t}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>
            </Box>

            <Drawer
                anchor="right"
                open={isModalOpen}
                onClose={handleClose}
            >
                <Box sx={{ width: { xs: '100%', sm: 600 }, p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {/* Header */}
                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'primary.main', color: 'white' }}>
                        <Typography variant="h6">{selectedId ? 'Edit Slot' : 'Add Slot'}</Typography>
                        <IconButton onClick={handleClose} sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto' }}>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <Grid container spacing={2}>

                                {/* Slot Type */}
                                {!isDoctor && <Grid item xs={12}>
                                    <FormControl fullWidth margin="dense" size="small">
                                        <InputLabel id="slot-type-label">Slot Type</InputLabel>
                                        <Select
                                            labelId="slot-type-label"
                                            id="slotType"
                                            value={slotData.slotType}
                                            label="Slot Type"
                                            onChange={(e) => handleChange("slotType", e.target.value)}
                                        >
                                            {SLOT_TYPES.map((type) => (
                                                <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>}

                                {/* Conditional Fields based on Slot Type */}
                                {slotData.slotType === 'consultation' && !isDoctor && (
                                    <Grid item xs={12}>
                                        <Autocomplete
                                            fullWidth
                                            size="small"
                                            options={specialistOptions}
                                            disabled={isLoadingSpecialists}
                                            value={specialistOptions.find((option) => option.value === (slotData.specialistId || '')) || null}
                                            onChange={(_event, option) => handleChange("specialistId", option?.value || '')}
                                            isOptionEqualToValue={(option, value) => option.value === value.value}
                                            getOptionLabel={(option) => option.label}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    margin="dense"
                                                    label="Specialist"
                                                    error={!!fieldErrors.specialistId}
                                                    helperText={fieldErrors.specialistId}
                                                />
                                            )}
                                        />
                                    </Grid>
                                )}

                                {slotData.slotType === 'wellnessPackage' && (
                                    <Grid item xs={12}>
                                        <FormControl fullWidth margin="dense" size="small" error={!!fieldErrors.wellnessPackageId}>
                                            <InputLabel id="wellness-package-label">Wellness Package</InputLabel>
                                            <Select
                                                labelId="wellness-package-label"
                                                id="wellnessPackageId"
                                                value={slotData.wellnessPackageId || ''}
                                                label="Wellness Package"
                                                onChange={(e) => handleChange("wellnessPackageId", e.target.value)}
                                            >
                                                {wellnessPackages.map((pkg) => (
                                                    <MenuItem key={pkg._id} value={pkg._id}>
                                                        {pkg.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {fieldErrors.wellnessPackageId && (
                                                <FormHelperText>{fieldErrors.wellnessPackageId}</FormHelperText>
                                            )}
                                        </FormControl>
                                    </Grid>
                                )}

                                <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 1, fontWeight: 'bold' }}>Timing Details</Typography>
                                </Grid>

                                {/* Date */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        margin="dense"
                                        id="date"
                                        label="Date"
                                        type="date"
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        InputLabelProps={{ shrink: true }}
                                        value={slotData.date}
                                        onChange={(e) => handleChange("date", e.target.value)}
                                        error={!!fieldErrors.date}
                                        helperText={fieldErrors.date}
                                    />
                                </Grid>

                                {/* Duration */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        margin="dense"
                                        id="duration"
                                        label="Duration (minutes)"
                                        type="number"
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        value={slotData.duration ?? ''}
                                        onChange={(e) => handleChange("duration", e.target.value === '' ? undefined : Number(e.target.value))}
                                        error={!!fieldErrors.duration}
                                        helperText={fieldErrors.duration}
                                    />
                                </Grid>

                                {/* Start Time */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        margin="dense"
                                        id="startTime"
                                        label="Start Time"
                                        type="time"
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        InputLabelProps={{ shrink: true }}
                                        value={slotData.startTime}
                                        onChange={(e) => handleChange("startTime", e.target.value)}
                                        error={!!fieldErrors.startTime}
                                        helperText={fieldErrors.startTime}
                                    />
                                </Grid>

                                {/* End Time */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        margin="dense"
                                        id="endTime"
                                        label="End Time"
                                        type="time"
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        InputLabelProps={{ shrink: true }}
                                        value={slotData.endTime}
                                        onChange={(e) => handleChange("endTime", e.target.value)}
                                        error={!!fieldErrors.endTime}
                                        helperText={fieldErrors.endTime}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 1, fontWeight: 'bold' }}>Availability & recurring</Typography>
                                </Grid>

                                {/* Max Bookings */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        margin="dense"
                                        id="maxBookings"
                                        label="Max Bookings"
                                        type="number"
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        value={slotData.maxBookings ?? ''}
                                        onChange={(e) => handleChange("maxBookings", e.target.value === '' ? undefined : Number(e.target.value))}
                                        error={!!fieldErrors.maxBookings}
                                        helperText={fieldErrors.maxBookings}
                                    />
                                </Grid>

                                {/* Is Available Checkbox */}
                                <Grid item xs={12} sm={6}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={slotData.isAvailable || false}
                                                onChange={(e) => handleChange("isAvailable", e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label="Is Available"
                                    />
                                </Grid>

                                {/* Is Recurring Checkbox */}
                                <Grid item xs={12} sm={6}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={slotData.isRecurring || false}
                                                onChange={(e) => handleChange("isRecurring", e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label="Recurring Slot"
                                    />
                                </Grid>

                                {/* Recurring Pattern */}
                                {slotData.isRecurring && (
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth margin="dense" size="small">
                                            <InputLabel id="recurring-pattern-label">Recurring Pattern</InputLabel>
                                            <Select
                                                labelId="recurring-pattern-label"
                                                id="recurringPattern"
                                                value={slotData.recurringPattern}
                                                label="Recurring Pattern"
                                                onChange={(e) => handleChange("recurringPattern", e.target.value)}
                                            >
                                                {RECURRING_PATTERNS.map((pattern) => (
                                                    <MenuItem key={pattern.value} value={pattern.value}>{pattern.label}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                )}

                            </Grid>
                        </form>
                    </Box>

                    <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button onClick={handleClose} variant="outlined" color="error">Cancel</Button>
                        <Button onClick={handleSubmit(onSubmit)} variant="contained" color="primary">
                            Submit
                        </Button>
                    </Box>
                </Box>
            </Drawer>
        </div>
    );
}
